import { StrictMode, Suspense, lazy } from 'react'
import { createRoot } from 'react-dom/client'
import { Provider } from 'react-redux'
import './styles/globals.css'
import { store } from './store/index.js'
import { TooltipProvider, Toaster, Spinner } from './components/index.js'
import { App } from './App.js'

/**
 * One build, four audiences, decided by the path before anything else runs.
 *
 * The desk is the root. Three other things live here and are not the desk: a
 * professional claiming an invitation at `/provider/set-password`, a guest instructor
 * opening a class at `/teach`, and — the third this file's own note predicted — a
 * brand-new administrator claiming theirs at `/team/set-password`. All three arrive
 * from an emailed link with a token, none of them has an account on this desk yet, and
 * none of them should ever see a rail.
 *
 * **Decided here rather than inside `App`** so that none of the three runs the desk's
 * session restore, subscribes to its permissions query, or pulls its screens into the
 * first chunk. `App` is the desk; these are their own pages that happen to share its
 * component layer, its HTTP client and its store.
 *
 * Read from `location.pathname` because there is still no router, which is the same
 * reason `/reset-password?token=` is read the way it is.
 */
const ProviderSignUp = lazy(() =>
  import('./screens/ProviderSignUp/ProviderSignUp.js').then((m) => ({ default: m.ProviderSignUp })),
)
const TeachingLink = lazy(() =>
  import('./screens/Teaching/TeachingLink.js').then((m) => ({ default: m.TeachingLink })),
)
const TeamInvite = lazy(() =>
  import('./screens/TeamInvite/TeamInvite.js').then((m) => ({ default: m.TeamInvite })),
)

function Root() {
  const path = window.location.pathname.replace(/\/+$/, '')
  const token = new URLSearchParams(window.location.search).get('token') ?? ''

  if (path === '/provider/set-password') {
    return (
      <Suspense fallback={<Spinner label="Opening your invitation" />}>
        <ProviderSignUp token={token} />
      </Suspense>
    )
  }
  if (path === '/teach') {
    return (
      <Suspense fallback={<Spinner label="Opening the class" />}>
        <TeachingLink token={token} />
      </Suspense>
    )
  }
  if (path === '/team/set-password') {
    return (
      <Suspense fallback={<Spinner label="Opening your invitation" />}>
        <TeamInvite token={token} />
      </Suspense>
    )
  }
  return <App />
}

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <Provider store={store}>
      <TooltipProvider>
        <Root />
        <Toaster />
      </TooltipProvider>
    </Provider>
  </StrictMode>,
)
